import React, { useState } from 'react'
import {
  Button,
  Caption1,
  Field,
  Input,
  Link,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Title3,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components'
import {
  LockClosedRegular,
  MailRegular,
  ArrowEnterRegular,
} from '@fluentui/react-icons'
import { useAuth } from '@templatean/shared/hooks'
import { LoginRequest } from '@templatean/shared/types'
import { validators } from '@templatean/shared/utils'

const WEB_APP_URL = import.meta.env.VITE_APP_URL || 'https://templatean.com'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
    rowGap: tokens.spacingVerticalL,
  },
  // The add-in names itself with its mark, as Office panes do.
  brand: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalS,
    textAlign: 'center',
  },
  logo: { width: '40px', height: '40px', display: 'block' },
  lede: { color: tokens.colorNeutralForeground2, maxWidth: '260px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  full: { width: '100%' },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalXS,
    textAlign: 'center',
  },
  muted: { color: tokens.colorNeutralForeground3, maxWidth: '280px' },
})

/**
 * Email and password sign-in, rendered by both the full app and the task pane
 * whenever there is no session.
 *
 * Follows the Office task pane guidance: the add-in names itself with its mark and a
 * one-line statement of what signing in gives you, fields carry visible labels and
 * report their own errors, the primary action fills the width and states what it is
 * doing, and a failure is a message bar rather than a bare line of red text.
 */
const LoginPage: React.FC = () => {
  const styles = useStyles()
  const { login, isLoading, error } = useAuth()
  const [credentials, setCredentials] = useState<LoginRequest>({ email: '', password: '' })
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!credentials.email) {
      errors.email = 'Enter the email you use for Templatean.'
    } else if (!validators.email(credentials.email)) {
      errors.email = 'That does not look like an email address.'
    }

    if (!credentials.password) {
      errors.password = 'Enter your password.'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validateForm()) return

    try {
      await login(credentials)
    } catch (err) {
      // The hook surfaces the message via `error`; nothing else to do here.
      console.error('Login failed:', err)
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.brand}>
        <svg className={styles.logo} viewBox="0 0 28 28" role="img" aria-label="Templatean logo">
          <rect width="28" height="28" rx="6" fill={tokens.colorBrandBackground} />
          <path d="M7 9.5h14v3h-5.5V21h-3v-8.5H7z" fill={tokens.colorNeutralForegroundOnBrand} />
        </svg>
        <Title3 as="h1">Templatean for Word</Title3>
        <Caption1 className={styles.lede}>
          Sign in to use your organisation&rsquo;s templates, assets and writing assistance in this
          document.
        </Caption1>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className={styles.card}>
          {error && (
            <MessageBar intent="error" politeness="assertive">
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          )}

          <Field
            label="Email"
            required
            validationState={validationErrors.email ? 'error' : 'none'}
            validationMessage={validationErrors.email}
          >
            <Input
              className={styles.full}
              type="email"
              autoComplete="username"
              contentBefore={<MailRegular />}
              placeholder="you@company.com"
              value={credentials.email}
              disabled={isLoading}
              onChange={(_, data) => setCredentials({ ...credentials, email: data.value })}
            />
          </Field>

          <Field
            label="Password"
            required
            validationState={validationErrors.password ? 'error' : 'none'}
            validationMessage={validationErrors.password}
          >
            <Input
              className={styles.full}
              type="password"
              autoComplete="current-password"
              contentBefore={<LockClosedRegular />}
              placeholder="Your password"
              value={credentials.password}
              disabled={isLoading}
              onChange={(_, data) => setCredentials({ ...credentials, password: data.value })}
            />
          </Field>

          <Button
            type="submit"
            appearance="primary"
            className={styles.full}
            disabled={isLoading}
            icon={isLoading ? <Spinner size="tiny" /> : <ArrowEnterRegular />}
          >
            {isLoading ? 'Signing in' : 'Sign in'}
          </Button>

          <Link
            href={`${WEB_APP_URL}/forgot-password`}
            target="_blank"
            rel="noreferrer"
            appearance="subtle"
          >
            Forgot your password?
          </Link>
        </div>
      </form>

      <div className={styles.footer}>
        <Text size={200} className={styles.muted}>
          Need an account? Ask your Templatean administrator, or visit{' '}
          <Link href={WEB_APP_URL} target="_blank" rel="noreferrer">
            templatean.com
          </Link>
          .
        </Text>
      </div>
    </div>
  )
}

export default LoginPage
